import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  ActivitiesShopSelectors,
  parseStartTime,
} from "@b2bportal/core-activities";
import { IconNameEnum } from "@b2bportal/core-themes";
import { InfoBannerSeverity } from "@b2bportal/core-ui";
import { useI18nContext } from "@hopper-b2b/i18n";
import dayjs from "dayjs";

// Pure function - determine severity and icon based on refund percentage
const getSeverityAndIcon = (percentageRefundable: number | undefined) => {
  if (percentageRefundable === undefined) {
    // Generic policy text (no percentage info)
    return {
      severity: InfoBannerSeverity.INFO,
      iconName: IconNameEnum.info,
    };
  }
  if (percentageRefundable === 100) {
    // Free cancellation
    return {
      severity: InfoBannerSeverity.SUCCESS,
      iconName: IconNameEnum.checkFilled,
    };
  }
  if (percentageRefundable === 0) {
    // Non-refundable
    return {
      severity: InfoBannerSeverity.ERROR,
      iconName: IconNameEnum.info,
    };
  }
  // Partial refund
  return {
    severity: InfoBannerSeverity.WARNING,
    iconName: IconNameEnum.info,
  };
};

export const useCancellationBanner = (options?: {
  includeDeadline?: boolean;
}) => {
  const includeDeadline = options?.includeDeadline ?? false;
  const { t, formatDateTime, DateTimeFormatStyle } = useI18nContext();
  const cancellationPolicy = useSelector(
    ActivitiesShopSelectors.getCancellationPolicy
  );
  const selectedDate = useSelector(ActivitiesShopSelectors.getSelectedDate);
  const selectedTimeSlot = useSelector(
    ActivitiesShopSelectors.getSelectedTimeSlot
  );

  const { text, percentageRefundable } = useMemo(() => {
    const defaultText = cancellationPolicy?.cancellationPolicy ?? null;
    const percentageRefundable = cancellationPolicy?.percentageRefundable;

    if (cancellationPolicy == null) {
      return {
        text: { cancellationPolicy: defaultText },
        percentageRefundable: undefined,
      };
    }

    if (!includeDeadline) {
      return {
        text: { cancellationPolicy: defaultText },
        percentageRefundable,
      };
    }

    // Number of days before the activity start that the policy window ends (always an integer)
    const policyEndsXDaysBefore = cancellationPolicy.policyEndsXDaysBefore;
    const startTimeStr = selectedTimeSlot?.startTime ?? null;
    const cancellationPolicyStringIncludesDateAndTime =
      selectedDate !== null &&
      selectedDate !== "" &&
      startTimeStr !== null &&
      policyEndsXDaysBefore >= 0;

    if (!cancellationPolicyStringIncludesDateAndTime) {
      return {
        text: { cancellationPolicy: defaultText },
        percentageRefundable,
      };
    }

    const parsed = parseStartTime(startTimeStr);
    if (!parsed) {
      return {
        text: { cancellationPolicy: defaultText },
        percentageRefundable,
      };
    }

    // Note: This calculation assumes selectedDate and startTime are already in the activity's local timezone.
    // The formatted output explicitly indicates "(local time)" to the user.
    const activityStart = dayjs(selectedDate)
      .hour(parsed.hours)
      .minute(parsed.minutes)
      .second(0)
      .millisecond(0);
    const cutoff = activityStart.subtract(policyEndsXDaysBefore, "day");
    const timeFormatted = formatDateTime(
      cutoff.toDate(),
      DateTimeFormatStyle.ShortTime
    );
    const dateFormatted = formatDateTime(
      cutoff.toDate(),
      DateTimeFormatStyle.ShortMonthDayShortWeekday
    );

    if (percentageRefundable === 100) {
      return {
        text: {
          cancellationPolicy: t(
            "core-activities.shopPage.freeCancellationBanner.freeCancellationBefore",
            { date: dateFormatted, time: timeFormatted }
          ),
        },
        percentageRefundable,
      };
    }

    if (percentageRefundable === 0) {
      return {
        text: {
          cancellationPolicy: t(
            "core-activities.shopPage.freeCancellationBanner.nonRefundable"
          ),
        },
        percentageRefundable,
      };
    }

    return {
      text: {
        cancellationPolicy: t(
          "core-activities.shopPage.freeCancellationBanner.partialRefundBefore",
          {
            percentage: percentageRefundable,
            date: dateFormatted,
            time: timeFormatted,
          }
        ),
      },
      percentageRefundable,
    };
  }, [
    cancellationPolicy,
    includeDeadline,
    selectedTimeSlot?.startTime,
    selectedDate,
    formatDateTime,
    DateTimeFormatStyle.ShortTime,
    DateTimeFormatStyle.ShortMonthDayShortWeekday,
    t,
  ]);

  return { text, percentageRefundable, getSeverityAndIcon };
};
